import { Component, Input, OnChanges, SimpleChanges, ViewChild, OnInit, ViewChildren, QueryList, AfterViewInit } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';

import { Align, FilterType, TableDataType, TableEvent, TableLayout } from 'src/app/shared/enums';
import { FilterHeaderComponent } from './filter-header/filter-header.component';
import { HtmlFormatterComponent } from './html-formatter/html-formatter.component';
import { TopTableComponent } from './top-table/top-table.component';
import { TableService } from 'src/app/services/table.service';
import { MaterialModule } from 'src/app/material.module';
import { ICell, IColumnConfig, IExpandConfig, IGroupColumnConfig, IRetrieveParam, IRightClickCellData, ISelectionOption, ISortConfig, ITableConfig, ITopOptions } from 'src/app/shared/interfaces/ITable';
import { formatDateOutput } from 'src/app/shared/utils';
import { TABLE_STYLES, FILTER_COLUMN_TYPE, PARAM_OPERATOR } from 'src/app/shared/constants';
import { TableActionsComponent } from './table-actions/table-actions.component';
import { IconModule } from 'src/app/icon/icon.module';
@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    MaterialModule,
    CommonModule,
    TranslateModule,
    DragDropModule,
    FilterHeaderComponent,
    HtmlFormatterComponent,
    TopTableComponent,
    TableActionsComponent,
    IconModule
  ],
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0px', padding: '0' })),
      state('expanded', style({ height: 'auto', paddingBottom: '4px' })),
      state('expanded-no-padding', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('expanded-no-padding <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})
export class DataTableComponent implements OnChanges, OnInit, AfterViewInit {
  readonly TableLayout = TableLayout;
  @ViewChildren('filterHeader') allFilterHeaderComponent: QueryList<FilterHeaderComponent>;
  @ViewChild(MatSort) sort: MatSort = new MatSort();
  @ViewChild(MatPaginator) paginator: MatPaginator | any;
  @ViewChild(TopTableComponent) topTable: TopTableComponent | any;
  @Input() tableId?: string;
  @Input() topOptions?: ITopOptions;
  @Input() parentTable?: DataTableComponent;
  @Input() fetchTableData?: (params: IRetrieveParam) => Observable<any>;
  @Input() autoRetrieve: boolean = true;
  @Input() dataSource: any[] = [];
  @Input() params: IRetrieveParam = { page: 1, pageSize: 10, sortText: '', globalSearch: '' };
  @Input() onLoadedData?: (response: any) => any;
  @Input() groupColumnConfig: IGroupColumnConfig[][]; // support multi level group
  @Input() tableConfig?: ITableConfig;
  @Input() defaultSortIcon: SortDirection = 'asc'; // default short when short on new column
  @Input() selectionOptions?: ISelectionOption;
  @Input() pageSizeOption = [10, 20, 50, 100];
  @Input() expandConfig: IExpandConfig;
  @Input() onDragEnd?: (dataSource: any[]) => void;
  @Input() onClickRow?: (row: any) => void;
  @Input() keepAppliedSearch: boolean;

  externalParams: any = {};
  columnFilterParams: any = {};
  customColumnFilters: { [key: string]: string } = {}; // filters from FilterType.CUSTOM headers
  filterV2Values: Record<string, unknown> = {};
  currentSortColumn: string;
  currentSortDirection: SortDirection;
  selection = new SelectionModel<any>(true, []);
  dataSourceSelection = new MatTableDataSource<any>([]);
  unsubscribe: Subscription;
  columnType = TableDataType; // because can't use direct DataType in html
  displayedColumns: string[] = [];
  displayedFilterColumns: string[] = []; // displayedColumnFilters
  displayedGroupColumns: string[][] = [];
  hasFilter: boolean;
  currentSearchText: string;
  keyword: string;
  currentColumnFilterText: { [key: string]: any } = {};
  currentColumnFilterOperator: { [key: string]: any } = {};
  totalRecord: number = 0;
  expandStatus: Map<any, any> = new Map<any, any>();
  isLoading: boolean;
  isResettingFilters = false;

  constructor(private tableService: TableService) { }

  get isDraggable() {
    if (typeof this.tableConfig?.draggable == 'function') {
      return this.tableConfig?.draggable();
    } else if (typeof this.tableConfig?.draggable == 'boolean') {
      return this.tableConfig?.draggable;
    }
    return false;
  }

  get isModernLayout(): boolean {
    return this.tableConfig?.layout === TableLayout.MODERN;
  }

  get loading() {
    return this.isLoading
  }

  get modernTopActions() {
    return this.topOptions?.topActions?.filter(action => !action.selectionAction);
  }

  get selectionActions() {
    return this.topOptions?.topActions?.filter(action => action.selectionAction) ?? [];
  }

  get RetrieveDataParams() {
    // Keep search param when back
    if (this.keepAppliedSearch && this.tableId) {
      const savedState = this.tableService.consumeSearchState(this.tableId);
      if (savedState) {
        this.currentSearchText = savedState.globalSearchValue;
        this.keyword = savedState.globalSearchValue;
        this.currentColumnFilterText = { ...savedState.columnFilterText };
        if (Object.keys(savedState.columnFilterOperator).length !== 0) {
          this.currentColumnFilterOperator = { ...savedState.columnFilterOperator };
        }
        if (Object.keys(savedState.globalSearch).length !== 0) {
          this.params = { ...savedState.globalSearch } as IRetrieveParam;
        }
        if (Object.keys(savedState.filterText).length !== 0) {
          this.columnFilterParams = { ...savedState.filterText };
        }
        this.customColumnFilters = { ...savedState.customColumnFilters };
        if (this.isModernLayout) {
          this.filterV2Values = { ...savedState.customColumnFilterValues };
        }
      }

      this.allFilterHeaderComponent?.forEach((item: any) => {
        if (item.column?.key) {
          this.currentColumnFilterText[item.column.key] = item.filterText;
          this.currentColumnFilterOperator[item.column.key] = item.columnFilter;
        }
      });

      this.tableService.saveSearchState(this.tableId, {
        globalSearchValue: this.topTable?.currentSearchText ?? this.currentSearchText ?? '',
        globalSearch: this.params,
        filterText: this.columnFilterParams,
        columnFilterText: this.currentColumnFilterText,
        columnFilterOperator: this.currentColumnFilterOperator,
        customColumnFilters: this.customColumnFilters,
        customColumnFilterValues: this.isModernLayout ? this.filterV2Values : {}
      });
    }
    return { ...this.params, ...this.externalParams, ...this.columnFilterParams };
  }

  set ExternalParams(newParams: any) {
    this.externalParams = { ...this.externalParams, ...newParams };
  }

  set SortColumn(sortColumn: ISortConfig) {
    this.sort.direction = this.currentSortDirection = sortColumn.sortDirection;
    this.currentSortColumn = sortColumn.columnName;
    this.params.sortText = `${this.currentSortColumn}.${this.sort.direction}`;
  }

  get IsAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSourceSelection.data.length;
    return numSelected === numRows;
  }

  ngOnInit() {
    this.initTableConfig();
    this.sort.start = this.defaultSortIcon;
    //no need to ClearAllColumnFilter on init
    this.autoRetrieve && this.retrieveData(true, false);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['tableConfig']) {
      this.selection.clear();
      this.initTableConfig();
    }
    // changes['tableConfig'] && this.selection.clear() && this.initTableConfig();
    // total only get from dataSource if data not from API
    // check dataSource change and have value
    if (changes['dataSource'] && this.dataSource) {
      !this.fetchTableData && (this.totalRecord = this.dataSource.length);
      this.setDefaultSelection();
    }
  }

  initTableConfig() {
    this.displayedColumns = this.tableConfig?.columnConfig?.map((column) => column.key) ?? [];
    if (this.isModernLayout) {
      const inlineActionIndex = this.displayedColumns.indexOf('inline-action');
      if (inlineActionIndex >= 0) {
        const [inlineActionColumn] = this.displayedColumns.splice(inlineActionIndex, 1);
        this.displayedColumns.push(inlineActionColumn);
      }
    }
    this.hasFilter = !this.isModernLayout
      && (this.tableConfig?.columnConfig?.some((column) => column.filterable !== undefined && column.filterable !== FilterType.NONE) ?? false);
    this.displayedGroupColumns = this.groupColumnConfig?.map((columnConfig) => columnConfig.map((column) => column.key));
    this.selectionOptions && this.displayedColumns.splice(this.selectionOptions?.position ?? 0, 0, 'col-selection');
    this.displayedFilterColumns = this.displayedColumns.map((column) => `${column}-filter`);
  }

  ngAfterViewInit() {
    this.topTable.initSearch();
  }

  setDefaultSelection() {
    this.dataSourceSelection = new MatTableDataSource<any>(this.dataSource || []);
    this.selection.select(...(this.selectionOptions?.getDefaultSelection?.(this.dataSourceSelection.data) ?? []));
  }

  /**
   * Retrieves data for the table, optionally resetting the current page and clearing column filters.
   *
   * @param {boolean} [isResetPage=true] - Indicates whether to reset the current page to the first page.
   * @param {boolean} [isClearAllColumnFilter=true] - Indicates whether to clear all column filters before retrieving data.
   */
  retrieveData(isResetPage: boolean = true, isClearAllColumnFilter: boolean = true) {
    this.selection.clear();
    this.expandStatus.clear();
    isClearAllColumnFilter && this.onClearAllColumnFilter();
    if (this.fetchTableData) {
      isResetPage && this.resetPage();
      this.dataSource = [];
      this.unsubscribe?.unsubscribe();
      this.isLoading = true;
      this.unsubscribe = this.fetchTableData(this.RetrieveDataParams).subscribe((response) => {
        this.isLoading = false;
        const dataSource = response.data ?? response;
        // Apply mapApiResponse if it exists
        this.dataSource = this.onLoadedData ? this.onLoadedData(dataSource) : dataSource;
        this.setDefaultSelection();
        this.totalRecord = response.totalRecord || 0;
      });
    }
  }

  handleFilterTable = (filterText: string) => {
    this.ExternalParams = { filterText };
    this.retrieveData(true, false);
  }

  handleFilterV2(filters: Record<string, unknown>) {
    this.filterV2Values = { ...filters };
    const filterText = Object.entries(filters)
      .flatMap(([key, value]) => {
        const column = this.tableConfig?.columnConfig?.find(item => item.key === key);
        const filterKey = column?.filterKey ?? key;

        if (column?.filterable === FilterType.DATE_RANGE) {
          const range = value as { start?: Date; end?: Date };
          return [
            range.start ? `${filterKey}[${PARAM_OPERATOR.GREATER_THAN_OR_EQUAL}]${formatDateOutput(range.start)}[DATE]` : null,
            range.end ? `${filterKey}[${PARAM_OPERATOR.LESS_THAN_OR_EQUAL}]${formatDateOutput(range.end)}[DATE]` : null
          ];
        }

        if (column?.filterable === FilterType.DATE) {
          return [`${filterKey}[${PARAM_OPERATOR.EQUAL}]${formatDateOutput(value as Date)}[DATE]`];
        }

        if (column?.filterable === FilterType.MULTI_SELECT && Array.isArray(value)) {
          return value.length ? [`${filterKey}[${PARAM_OPERATOR.IN}](${value.join(', ')})`] : [];
        }

        const operator = column?.filterable === FilterType.TEXT ? PARAM_OPERATOR.LIKE : PARAM_OPERATOR.EQUAL;
        return [`${filterKey}[${operator}]${value}`];
      })
      .filter((filter): filter is string => !!filter)
      .join('&');

    this.columnFilterParams.filterText = filterText;
    this.retrieveData(true, false);
  }

  onClearAllColumnFilter() {
    this.customColumnFilters = {};
    this.isResettingFilters = true;
    this.allFilterHeaderComponent.forEach((component) => component.clearFilter());
    this.isResettingFilters = false;
    this.columnFilterParams.filterText = '';
  }

  masterToggle() {
    if (this.IsAllSelected) {
      this.selection.clear();
    } else {
      this.selection.select(...this.dataSourceSelection.data);
    }
    this.selectionOptions?.onSelectAll?.();
  }

  rowToggle(row: any) {
    this.selection.toggle(row);
    this.selectionOptions?.onSelectRow?.(row);
  }

  resetPage() {
    this.params.page = 1;
    this.paginator && this.paginator.firstPage();
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.dataSource, event.previousIndex, event.currentIndex);
    this.dataSource = [...this.dataSource];
    this.onDragEnd?.(this.dataSource);
  }

  //Close all expansion panels before dragging
  //There is an animation conflict error
  onDragStart() { }

  getTitleValue(column: IColumnConfig) {
    return typeof column.title === 'function' ? column.title(column) : column.title;
  }

  // Each cell in the same column can have a different type
  getCellDataType(cell: ICell) {
    const { column } = cell;
    return typeof column.cellDataType === 'function' ? column.cellDataType(cell) : column.cellDataType;
  }

  getCellStyle(cell: ICell) {
    const { column } = cell;
    return typeof column.cellStyle === 'function' ? column.cellStyle(cell) : column.cellStyle;
  }

  getCellValue(cell: ICell) {
    const { row, column } = cell;
    return typeof column.cellValue === 'function' ? column.cellValue(cell) : row[cell.column.key];
  }

  getRowStyle(row: any) {
    const styles: any = {};
    if (this.isModernLayout && this.selection.isSelected(row)) {
      styles['modern-selected-row'] = true;
    }
    if (this.tableConfig?.tableStyles?.includes(TABLE_STYLES.STRIPED) || !this.tableConfig?.tableStyles) {
      const index = this.dataSource.indexOf(row);
      styles['striped-row'] = index % 2 !== 0;
      styles['striped'] = true;
    }
    if (this.tableConfig?.tableStyles?.includes(TABLE_STYLES.SOLID)) {
      styles['solid-row'] = true;
    }
    if (!this.tableConfig?.tableStyles?.includes(TABLE_STYLES.EXPAND_TREE) && this.expandStatus.get(row.id)) {
      styles['container-expand'] = true;
    }
    return styles;
  }

  isModernInlineActionColumn(column: IColumnConfig): boolean {
    return this.isModernLayout && column.key === 'inline-action';
  }

  // can't use titleStyle, because of mat-sort-header
  getTitleAlign(align: Align | undefined): string {
    switch (align) {
      case Align.CENTER:
        return 'centered-sorted-header';
      case Align.RIGHT:
        return 'right-sorted-header';
      default:
        return '';
    }
  }

  onSortChange() {
    if (this.currentSortColumn === this.sort.active) {
      this.sort.direction = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortColumn = this.sort.active;
      // default sorting when changing columns
      this.sort.direction = this.defaultSortIcon;
    }
    this.currentSortDirection = this.sort.direction;
    this.params.sortText = this.sort.direction ? `${this.currentSortColumn}.${this.sort.direction}` : '';
    this.retrieveData(true, false);
  }

  onPageChange(event: PageEvent) {
    this.params.page = event.pageIndex + 1;
    this.params.pageSize = event.pageSize;
    this.retrieveData(false, false);
  }

  onRightClickCell(event: any, cell: ICell) {
    event.preventDefault();
    const eventData: IRightClickCellData = { event, cell };
    this.tableService.tableSubject.next({ eventType: TableEvent.RIGHT_CLICK_CELL, eventData });
  }

  onClickRowEvent(row: any, column: any) {
    if (column.onClickCell) {
      column.onClickCell({ row, column });
    } else if (this.onClickRow && !column.disableRowClick) {
      this.onClickRow({ row, column });
    }
  }

  columnFilterEvent(param: any) {
    if (param.type == FILTER_COLUMN_TYPE.RESET) {
      this.retrieveData();
    } else if (param.type == FILTER_COLUMN_TYPE.FILTER) {
      this.applyColumnFilters();
    }
  }

  /**
   * Applies a filter coming from a custom filter header (FilterType.CUSTOM).
   * Stores the value per column key and rebuilds the combined filter text.
   *
   * @param key - column key
   * @param value - selected value (null/'' clears the filter for this column)
   * @param operator - param operator, defaults to 'equal'
   */
  setCustomColumnFilter(key: string, value: any, operator: string = 'equal') {
    const isEmpty = (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0));
    if (isEmpty) {
      delete this.customColumnFilters[key];
    } else {
      const filterValue = Array.isArray(value) ? `(${value.join(', ')})` : value;
      this.customColumnFilters[key] = `${key}[${operator}]${filterValue}`;
    }
    this.applyColumnFilters();
  }

  private applyColumnFilters() {
    if (this.isResettingFilters) return;
    const headerFilters = this.allFilterHeaderComponent
      .map((component) => {
        const keyword = component.filterText;
        const operator = component.columnFilter;
        const key = component.column.key;
        // for date
        if (this.tableConfig?.columnConfig?.find((config) => config.key === key)?.filterable === FilterType.DATE) {
          return keyword && operator ? `${key}[${operator.paramOperator}]${formatDateOutput(keyword)}[DATE]` : null;
        }
        // for text and number
        return keyword && operator ? `${key}[${operator.paramOperator}]${keyword}` : null;
      })
      .filter(Boolean);

    const filterText = [...headerFilters, ...Object.values(this.customColumnFilters)].filter(Boolean).join('&');

    this.columnFilterParams.filterText = filterText;
    this.retrieveData(true, false);
  }

  getExpandClass() {
    const classNames = [];
    if (this.expandConfig.class) {
      classNames.push(this.expandConfig.class);
    }
    const expandClass = this.tableConfig?.tableStyles?.includes(TABLE_STYLES.EXPAND_TREE) ? 'tree-expand' : 'container-expand';
    classNames.push(expandClass);

    return classNames;
  }
}
